/**
 * Normalización de texto para buscar.
 *
 * En la calle nadie escribe con tildes ni respeta mayúsculas. "Café Los
 * Álamos" se busca como "cafe los alamos". Guardamos una versión plana del
 * nombre para que buscar así funcione.
 */
export function textoBusqueda(v) {
    return v
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .trim();
}
/**
 * ¿El término aparece en los campos? Tolerante a tildes y **por palabras**.
 *
 * Cada palabra del término tiene que estar en alguno de los campos, no
 * necesariamente en el mismo ni en ese orden: «berraca chapinero» encuentra
 * el pedido cuyo producto es la Berraca y cuyo destino está en Chapinero.
 * Antes se buscaba la frase entera dentro de un solo campo, y eso solo servía
 * si uno recordaba exactamente cómo quedó escrito.
 */
export function coincide(termino, ...campos) {
    const palabras = textoBusqueda(termino).split(' ').filter(Boolean);
    if (!palabras.length)
        return true;
    const heno = textoBusqueda(campos.filter((c) => typeof c === 'string' && c.length > 0).join(' \u0001 '));
    return palabras.every((w) => heno.includes(w));
}
/**
 * Todo lo que de un cliente sirve para encontrarlo.
 *
 * Quien busca no se acuerda del nombre exacto del negocio: se acuerda del NIT
 * que le dictaron, de la calle, del barrio, de que era «la del parque». Buscar
 * solo por nombre obliga a recordar precisamente lo que uno no recuerda.
 *
 * El barrio no es un campo aparte: viene dentro de la dirección, que es como la
 * gente la escribe. Buscar sobre el texto completo lo cubre sin inventar un
 * campo que nadie llenaría.
 */
export function camposDeCliente(c) {
    return [
        c.nombre_negocio, c.razon_social, c.codigo, c.documento, c.nit,
        c.direccion_corta, c.quien_decide, c.telefono_principal, c.notas,
        ...(c.etiquetas ?? []),
    ].filter(Boolean).join(' ');
}
/**
 * Qué tan bien cuadra un término con algo del catálogo, para ordenar sugerencias.
 *
 * `coincide` responde sí o no, y para una lista de treinta insumos eso basta.
 * Para escoger un renglón de una compra no: quien escribe «bolsa» quiere ver
 * primero «Bolsa 2 kg» y «Bolsa papel», y después «Caja con bolsa interior»,
 * no las tres revueltas por orden alfabético. Por eso esto devuelve un puntaje:
 * cuanto más temprano y más al nombre pegue, más alto.
 *
 * Reglas, de más a menos puntos: el nombre empieza por la palabra; la palabra
 * empieza alguna palabra del nombre; está en el medio del nombre; está en otro
 * campo (código, categoría, característica). **Todas** las palabras del término
 * tienen que aparecer en alguna parte, o el puntaje es cero. Un plural tolera
 * su singular —«bolsas» encuentra «Bolsa»— porque nadie escribe pensando en
 * cómo quedó guardado.
 *
 * Con el término vacío todo cuadra igual (1), para que la lista salga en el
 * orden que ya traía.
 */
export function puntajeBusqueda(termino, principal, ...otros) {
    const t = textoBusqueda(termino);
    if (!t)
        return 1;
    const nombre = textoBusqueda(principal);
    const resto = otros.filter((c) => !!c).map((c) => textoBusqueda(c));
    let puntos = 0;
    for (const palabra of t.split(' ')) {
        const formas = raicesDe(palabra);
        const mejor = Math.max(...formas.map((w) => puntajeDePalabra(w, nombre, resto)));
        if (mejor <= 0)
            return 0;
        puntos += mejor;
    }
    // La frase entera al principio del nombre vale más que sus palabras sueltas.
    if (nombre.startsWith(t))
        puntos += 30;
    if (nombre === t)
        puntos += 50;
    return puntos;
}
function puntajeDePalabra(w, nombre, resto) {
    if (nombre.startsWith(w))
        return 60;
    if (nombre.includes(' ' + w))
        return 40;
    if (nombre.includes(w))
        return 20;
    if (resto.some((c) => c.startsWith(w) || c.includes(' ' + w) || c.includes('-' + w)))
        return 15;
    if (resto.some((c) => c.includes(w)))
        return 8;
    return 0;
}
/** «bolsas» → bolsas, bolsa; «panes» → panes, pane, pan. Solo para palabras con cuerpo. */
function raicesDe(w) {
    const formas = [w];
    if (w.length > 3 && w.endsWith('es'))
        formas.push(w.slice(0, -2));
    if (w.length > 3 && w.endsWith('s'))
        formas.push(w.slice(0, -1));
    return formas;
}
